import { useRouter } from 'next/router';
import { cx } from '@linaria/core';
import { textRegularLight } from 'styles/typography';
import { Link } from 'components/Link';
import { IconChevronDown } from 'components/Icons';
import { PopoverAction, usePopoverState } from 'components/Popover';
import { queryKeys, useQueryTE } from 'utils/react-query';
import { taskPageApiEffects as appPageApiEffects } from 'modules/Tasks/apiEffects';
import { pipe } from 'utils/fp';
import { either } from 'fp-ts';
import { Thumbnail } from 'components/Thumbnail';
import { CoverImageSize, constructCoverImageURL } from 'utils/coverImage';
import { baseTheme } from 'styles/utils';
import { IconChevronUp } from 'components/Icons/IconChevronUp';
import { useState } from 'react';
import { LockComponent } from 'components/List/components/Lock/Lock';
import { AppsListPopover } from '../../../../components/SidebarV2/AppsListPopover';
import { appInfoArea, shrinkText, appNameArea, appContainer } from '../../Sidebar.styles';

interface AppsDropdownProps extends React.HTMLAttributes<HTMLElement> {
  isCollapsed?: boolean;
  popoverClassName?: string;
}

export const AppsDropdown: React.FC<AppsDropdownProps> = ({ isCollapsed, popoverClassName, ...props }) => {
  const { appId = '', userOrOrgId } = useRouter().query as { appId: string; userOrOrgId: string };
  const popover = usePopoverState({ gutter: 4, placement: 'bottom-start' });
  const currentApp = useCurrentApp();
  const [isPopOverDisabled, setIsPopOverDisabled] = useState(false);

  return (
    <>
      <section className={appContainer} {...props}>
        <PopoverAction
          data-testid="apps-dropdown"
          popover={popover}
          data-collapsed={isCollapsed}
          className={appInfoArea}
          disabled={isPopOverDisabled}
        >
          <Thumbnail
            src={currentApp && constructCoverImageURL(currentApp, CoverImageSize.SMALL)}
            fallback={currentApp?.id}
            data-testid="app-thumbnail"
            className="app-thumbnail"
          />
          {!isCollapsed && (
            <>
              <div className={cx(appNameArea, 'app-titles')}>
                <span className={cx(textRegularLight, shrinkText, 'appid')}>{appId}</span>
                <Link href={`/${userOrOrgId}`} className={cx(textRegularLight, shrinkText, 'userid')}>
                  {userOrOrgId}
                </Link>
              </div>
              {isPopOverDisabled ? (
                <LockComponent tooltipText="User does not have other public Apps" />
              ) : popover.visible ? (
                <IconChevronUp color={baseTheme.light.colors.bluishGrey} size={24} />
              ) : (
                <IconChevronDown color={baseTheme.light.colors.bluishGrey} size={24} />
              )}
            </>
          )}
        </PopoverAction>
      </section>
      <AppsListPopover popover={popover} className={popoverClassName} setIsPopOverDisabled={setIsPopOverDisabled} />
    </>
  );
};

export function useCurrentApp() {
  const { appId = '', userOrOrgId } = useRouter().query as { appId: string; userOrOrgId: string };
  const { data: appE } = useQueryTE([queryKeys.Apps, { appId, userOrOrgId }], appPageApiEffects.fetchAppTE({ appId, userId: userOrOrgId }), {
    enabled: Boolean(appId && userOrOrgId),
  });

  return pipe(
    appE,
    either.getOrElseW(() => undefined),
  );
}
